// Bridge between the SW registration (registerSW.ts) and the update banner
// (components/UpdateAvailableBanner.tsx) — card Z-vt4W8TwYv5.
//
// In `prompt` mode, vite-plugin-pwa's `registerSW(...)` returns an
// `updateSW(reloadPage?: boolean)` function that posts SKIP_WAITING to the
// WAITING service worker and reloads the page once it takes control. That
// function is created deep inside registerSW.ts's dynamic import; we stash it
// here so the banner (mounted elsewhere in the tree) can trigger the update on
// tap without threading the function through React context.

type UpdateFn = (reload?: boolean) => Promise<void>

let _update: UpdateFn | null = null

export function setUpdateSW(fn: UpdateFn): void {
  _update = fn
}

/** Apply the waiting update and reload onto the new bundle. No-op if not ready. */
export function applyUpdate(): Promise<void> {
  return _update ? _update(true) : Promise.resolve()
}

// ── Office update state (card 5gncDWpbA9KV) ─────────────────────────────────
// Read by the office banner (components/themed/OfficeUpdateBanner.tsx), set by
// registerSW.ts. 'waiting' = a new SW is installed but held back (a field
// window was open); 'active' = a new SW already took over this page, which is
// still running the old bundle. Kept at module level so an update detected
// before the banner mounts isn't lost.

export type UpdateState = 'none' | 'waiting' | 'active'

let _state: UpdateState = 'none'
let _officeReloadRequested = false
const _listeners = new Set<() => void>()

export function setUpdateState(state: UpdateState): void {
  // Already on the new SW; nothing left to wait for.
  if (_state === 'active') {
    return
  }
  _state = state
  _listeners.forEach((l) => l())
}

export function getUpdateState(): UpdateState {
  return _state
}

export function subscribeUpdateState(listener: () => void): () => void {
  _listeners.add(listener)
  return () => {
    _listeners.delete(listener)
  }
}

export function isOfficeReloadRequested(): boolean {
  return _officeReloadRequested
}

/**
 * Office banner action. A held-back SW is activated first (the reload follows
 * on controllerchange in registerSW.ts); if it already took over, just reload.
 */
export function applyOfficeUpdate(): void {
  _officeReloadRequested = true
  if (_state === 'waiting' && _update) {
    void _update(true)
  } else {
    window.location.reload()
  }
}
